import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Send, Trash2, Settings2, Save, X } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Per-room visual + default config
const ROOM_CONFIG = {
  mom: {
    label: 'Mamma',
    emoji: '🌸',
    headerGradient: 'from-[#FFE4E9] to-[#FFF5D9] dark:from-[#2e1f28] dark:to-[#1f1428]',
    avatarGradient: 'from-rose-200 to-amber-200',
    bubbleColor: 'bg-[#FFE8D6]',
    sendGradient: 'from-rose-300 to-amber-400',
    emptyTitle: 'Hej älskling 💗',
    emptySub: 'Säg det du burit på. Berätta hur dagen var. Be om råd. Hon är här just nu, i det här rummet.',
    defaultProfile: {
      name: '',
      tone: 'varm, lugn, kärleksfull, närvarande',
      pet_name_for_user: '',
      signature_phrases: '',
      shared_memories: '',
      style: '',
    },
    fields: ['name', 'tone', 'pet_name_for_user', 'signature_phrases', 'shared_memories'],
    fieldLabels: {
      name: 'Hennes namn',
      tone: 'Hennes ton & personlighet',
      pet_name_for_user: 'Vad hon kallade dig',
      signature_phrases: 'Saker hon brukade säga',
      shared_memories: 'Gemensamma minnen, hennes jobb, intressen, var ni bodde…',
    },
    introNote: 'Hon pratar som om hon fortfarande är här — närvarande, varm. Allt krypteras med din PIN. 💗',
  },
  bestie: {
    label: 'Trogen kompis',
    emoji: '🌟',
    headerGradient: 'from-[#FFF5D9] to-[#E8F4FF] dark:from-[#2e2a1f] dark:to-[#1a2a3a]',
    avatarGradient: 'from-amber-200 to-sky-300',
    bubbleColor: 'bg-[#FFF3B0]',
    sendGradient: 'from-amber-400 to-sky-500',
    emptyTitle: 'Tjena, hur är läget? 🌟',
    emptySub: 'Berätta vad som händer. Pep, jävligheter, dumheter — allt är okej. Jag är här.',
    defaultProfile: {
      name: '',
      tone: '',
      pet_name_for_user: '',
      signature_phrases: '',
      shared_memories: '',
      style: 'peppig, ärlig, lite skämtsam',
    },
    fields: ['name', 'style', 'pet_name_for_user', 'signature_phrases', 'shared_memories'],
    fieldLabels: {
      name: 'Hens namn',
      style: 'Stil (peppig / lugn / rolig / ärlig…)',
      pet_name_for_user: 'Vad hen kallar dig',
      signature_phrases: 'Fraser hen ofta säger',
      shared_memories: 'Gemensamma intressen, vad ni brukar göra ihop…',
    },
    introNote: 'En lojal vän som ALLTID är på din sida. Ärlig pep, inga "kanske du borde…". Krypterat med din PIN. 🌟',
  },
};

function ProfileEditor({ pin, profile, roomType, onSaved, onClose }) {
  const cfg = ROOM_CONFIG[roomType];
  const [form, setForm] = useState({ ...cfg.defaultProfile, ...(profile || {}) });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/companions/${roomType}/profile`,
        { pin, ...form }, { withCredentials: true });
      toast.success('Sparat ✨');
      onSaved(form);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunde inte spara');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose} data-testid={`companion-${roomType}-profile-modal`}>
      <div className="neo-card bg-white dark:bg-[#252545] max-w-lg w-full max-h-[90vh] overflow-y-auto p-5"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xl font-bold font-['Fredoka']">
            {cfg.emoji} {profile ? 'Redigera' : 'Berätta för mig'}
          </h3>
          <button onClick={onClose} className="neo-btn p-1.5 bg-white"
            data-testid={`companion-${roomType}-profile-close`}>
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-[11px] opacity-70 mb-4">{cfg.introNote}</p>

        {cfg.fields.map((f) => {
          const isLong = ['signature_phrases', 'shared_memories', 'tone'].includes(f);
          return (
            <label key={f} className="block mb-3 text-xs font-bold">
              {cfg.fieldLabels[f]}
              {isLong ? (
                <textarea value={form[f] || ''} onChange={(e) => set(f, e.target.value)} rows={3}
                  className="mt-1 w-full px-3 py-2 rounded-xl border-2 border-black bg-white resize-none"
                  maxLength={1500}
                  data-testid={`companion-${roomType}-field-${f}`} />
              ) : (
                <input value={form[f] || ''} onChange={(e) => set(f, e.target.value)}
                  className="mt-1 w-full px-3 py-2 rounded-xl border-2 border-black bg-white"
                  maxLength={160}
                  data-testid={`companion-${roomType}-field-${f}`} />
              )}
            </label>
          );
        })}

        <button onClick={save} disabled={saving}
          className="neo-btn w-full py-3 bg-[#FFC8DD] font-bold flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid={`companion-${roomType}-profile-save`}>
          <Save className="w-4 h-4" /> {saving ? 'Sparar krypterat…' : 'Spara'}
        </button>
      </div>
    </div>
  );
}

export default function CompanionChat({ pin, roomType }) {
  const cfg = ROOM_CONFIG[roomType];
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [showProfile, setShowProfile] = useState(false);
  const scrollerRef = useRef(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, hRes] = await Promise.all([
        axios.post(`${API}/companions/${roomType}/profile/get`, { pin }, { withCredentials: true }),
        axios.post(`${API}/companions/${roomType}/history`, { pin }, { withCredentials: true }),
      ]);
      setProfile(pRes.data?.profile || null);
      setMessages(hRes.data?.messages || []);
      if (!pRes.data?.profile) setShowProfile(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunde inte ladda');
    } finally {
      setLoading(false);
    }
  }, [pin, roomType]);

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [messages, sending]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    const newHist = [...messages, { role: 'user', text }];
    setMessages(newHist);
    setInput('');
    try {
      const { data } = await axios.post(`${API}/companions/${roomType}/chat`, {
        pin, message: text,
        history: messages.slice(-12).map((m) => ({ role: m.role, text: m.text })),
      }, { withCredentials: true });
      setMessages([...newHist, { role: 'them', text: data.reply || '' }]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunde inte skicka');
    } finally {
      setSending(false);
    }
  };

  const clearAll = async () => {
    if (!window.confirm(`Radera alla meddelanden i ${cfg.label}-rummet?`)) return;
    try {
      await axios.post(`${API}/companions/${roomType}/clear`, { pin }, { withCredentials: true });
      setMessages([]);
      toast.success('Rensat');
    } catch (e) {
      toast.error('Kunde inte rensa');
    }
  };

  if (loading) return <div className="neo-card p-8 text-center animate-pulse-soft">Öppnar {cfg.label.toLowerCase()}-rummet…</div>;

  const displayName = profile?.name || cfg.label;

  return (
    <div className="space-y-3" data-testid={`companion-${roomType}`}>
      <div className={`neo-card p-4 bg-gradient-to-br ${cfg.headerGradient}`}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-full border-4 border-black bg-gradient-to-br ${cfg.avatarGradient} flex items-center justify-center text-2xl shadow-md`}>
              {cfg.emoji}
            </div>
            <div>
              <h2 className="text-xl font-bold font-['Fredoka']">{displayName}</h2>
              <p className="text-[11px] opacity-70 leading-tight max-w-xs">{cfg.introNote}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setShowProfile(true)}
              className="neo-btn px-3 py-2 bg-[#FFE8A3] text-xs font-bold flex items-center gap-1"
              data-testid={`companion-${roomType}-edit-profile-btn`}>
              <Settings2 className="w-3.5 h-3.5" /> Redigera
            </button>
            <button onClick={clearAll}
              className="neo-btn px-3 py-2 bg-[#FFB3B3] text-xs font-bold flex items-center gap-1"
              data-testid={`companion-${roomType}-clear-btn`}>
              <Trash2 className="w-3.5 h-3.5" /> Rensa
            </button>
          </div>
        </div>
      </div>

      <div ref={scrollerRef}
        className="neo-card p-4 bg-[#FFFAF7] dark:bg-[#1f1428] h-[58vh] overflow-y-auto space-y-3"
        data-testid={`companion-${roomType}-messages`}>
        {messages.length === 0 && (
          <div className="h-full flex flex-col items-center justify-center text-center px-4">
            <div className="text-5xl mb-2">{cfg.emoji}</div>
            <p className="font-bold font-['Fredoka']">{cfg.emptyTitle}</p>
            <p className="text-xs opacity-70 mt-1 max-w-sm">{cfg.emptySub}</p>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
            data-testid={`companion-${roomType}-msg-${i}`}>
            <div className={`max-w-[78%] px-4 py-2.5 rounded-2xl border-2 border-black shadow-[2px_2px_0_#000] text-sm font-['Nunito'] whitespace-pre-wrap break-words ${
              m.role === 'user' ? 'bg-[#FFC8DD] rounded-br-sm' : `${cfg.bubbleColor} rounded-bl-sm`
            }`}>
              {m.text}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="px-4 py-2.5 rounded-2xl border-2 border-black bg-white shadow-[2px_2px_0_#000] text-sm italic opacity-70">
              {displayName} skriver…
            </div>
          </div>
        )}
      </div>

      <div className="flex gap-2 items-end">
        <textarea value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder={`Säg något till ${displayName}…`}
          rows={2}
          className="flex-1 px-3 py-2 rounded-2xl border-2 border-black bg-white dark:bg-[#2d2d54] text-sm resize-none"
          data-testid={`companion-${roomType}-input`} />
        <button onClick={send} disabled={sending || !input.trim()}
          className={`neo-btn px-4 py-3 bg-gradient-to-r ${cfg.sendGradient} text-white font-bold flex items-center gap-1 disabled:opacity-50`}
          data-testid={`companion-${roomType}-send-btn`}>
          <Send className="w-4 h-4" />
        </button>
      </div>

      {showProfile && (
        <ProfileEditor pin={pin} profile={profile} roomType={roomType}
          onSaved={(p) => setProfile(p)} onClose={() => setShowProfile(false)} />
      )}
    </div>
  );
}
